
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const CantPayMortgagePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="What Happens If I Can't Pay My Mortgage | El Paso TX Guide | TREX RE LLC"
        description="Comprehensive guide on what happens when you can't pay your mortgage in Texas. Learn about foreclosure timelines, your legal rights, alternatives to foreclosure, and how to protect your home and credit in El Paso."
        keywords="can't pay mortgage El Paso, mortgage default Texas, foreclosure alternatives, stop foreclosure El Paso, mortgage hardship options"
        canonical="/elpaso-tx/cant-pay-mortgage"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "What Happens If I Can't Pay My Mortgage: Complete Texas Guide",
          "description": "Comprehensive guide on mortgage default consequences, foreclosure timeline, and alternatives for El Paso homeowners",
          "author": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          },
          "publisher": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          }
        }}
      />
      <GlobalNavbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b3e7887ef9b21018dda888c7d77e6179.jpg"
            alt="Homeowner struggling with mortgage payments"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/75 to-gray-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
              Mortgage Hardship Guide
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              What Happens If I Can&apos;t Pay My Mortgage?
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              A comprehensive guide for El Paso homeowners facing mortgage difficulties. Understand your options, protect your rights, and find solutions before it&apos;s too late.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call (915) 206-5814
              </a>
              <a
                href="/get-cash-offer-for-my-property"
                className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Get Cash Offer
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              Missing a mortgage payment can feel like the beginning of the end, but understanding what actually happens—and what options you have—can help you navigate this challenging situation. Whether you&apos;re facing a temporary setback or a long-term financial crisis, this guide will walk you through every step of the process and show you how to protect your home, your credit, and your future.
            </p>
            
            <p className="text-gray-700 leading-relaxed mb-8">
              In El Paso, Texas, thousands of homeowners face mortgage difficulties each year. Job loss, medical emergencies, divorce, and unexpected expenses can all lead to missed payments. The good news is that you have more options than you might think—and the sooner you act, the more options you&apos;ll have available.
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start">
                <i className="ri-alarm-warning-line text-2xl text-amber-600 mr-4 mt-1"></i>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Time Is Critical</h3>
                  <p className="text-gray-700">
                    The foreclosure process in Texas moves faster than in most states. Once you miss a payment, the clock starts ticking. Acting quickly gives you the most options and the best chance of a positive outcome.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Happens Timeline Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              The Mortgage Default <span className="text-teal-600">Timeline</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding what happens at each stage helps you know when to act and what to expect
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=calendar%20with%20marked%20dates%20and%20red%20warning%20circles%20showing%20mortgage%20payment%20deadlines%20financial%20documents%20and%20calculator%20on%20desk%20warm%20natural%20lighting%20professional%20business%20photography%20showing%20urgency&width=800&height=600&seq=mortgage-timeline-025&orientation=landscape"
                alt="Mortgage payment timeline calendar"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-green-500">
                <div className="flex items-center mb-3">
                  <span className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold mr-4">1</span>
                  <h3 className="text-xl font-bold text-gray-900">Days 1-15: Grace Period</h3>
                </div>
                <p className="text-gray-600">
                  Most mortgages include a 15-day grace period after the due date. During this time, you can make your payment without any late fees or negative consequences. Your credit score remains unaffected.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-yellow-500">
                <div className="flex items-center mb-3">
                  <span className="w-10 h-10 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center font-bold mr-4">2</span>
                  <h3 className="text-xl font-bold text-gray-900">Days 16-30: Late Fee Applied</h3>
                </div>
                <p className="text-gray-600">
                  After the grace period, a late fee is added to your account (typically 4-5% of your monthly payment). Your lender may begin calling to remind you about the missed payment. This is still considered a minor delinquency.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-orange-500">
                <div className="flex items-center mb-3">
                  <span className="w-10 h-10 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center font-bold mr-4">3</span>
                  <h3 className="text-xl font-bold text-gray-900">Days 30-60: Credit Impact Begins</h3>
                </div>
                <p className="text-gray-600">
                  At 30 days past due, your lender reports the delinquency to credit bureaus. Your credit score can drop 60-110 points. You&apos;ll receive more frequent calls and letters from your lender&apos;s collections department.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-red-500">
                <div className="flex items-center mb-3">
                  <span className="w-10 h-10 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-bold mr-4">4</span>
                  <h3 className="text-xl font-bold text-gray-900">Days 60-90: Demand Letters</h3>
                </div>
                <p className="text-gray-600">
                  Your lender sends formal demand letters requiring you to bring the loan current. Additional late fees and legal fees may be added. Your credit score continues to decline with each 30-day mark.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Continued Timeline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6 order-2 lg:order-1">
              <div className="bg-gray-50 p-6 rounded-xl shadow-md border-l-4 border-red-600">
                <div className="flex items-center mb-3">
                  <span className="w-10 h-10 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-bold mr-4">5</span>
                  <h3 className="text-xl font-bold text-gray-900">Days 90-120: Pre-Foreclosure</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  In Texas, your lender must send a Notice of Default and Intent to Accelerate at least 20 days before filing for foreclosure. This letter demands full payment of all past-due amounts and warns that foreclosure proceedings will begin if you don&apos;t respond.
                </p>
                <p className="text-gray-600">
                  This is a critical window—you still have time to work out a solution with your lender or explore alternatives like selling your home.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl shadow-md border-l-4 border-gray-800">
                <div className="flex items-center mb-3">
                  <span className="w-10 h-10 bg-gray-200 text-gray-800 rounded-full flex items-center justify-center font-bold mr-4">6</span>
                  <h3 className="text-xl font-bold text-gray-900">Days 120+: Foreclosure Filing</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  If you haven&apos;t resolved the default, your lender files a Notice of Acceleration and posts a Notice of Trustee&apos;s Sale. In Texas, foreclosure sales occur on the first Tuesday of each month at the county courthouse.
                </p>
                <p className="text-gray-600">
                  You must receive at least 21 days&apos; notice before the sale. Once the sale occurs, you typically have no redemption period in Texas—the new owner can begin eviction proceedings immediately.
                </p>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <img
                src="https://readdy.ai/api/search-image?query=texas%20courthouse%20exterior%20with%20foreclosure%20auction%20notice%20posted%20on%20bulletin%20board%20legal%20documents%20and%20gavel%20warm%20afternoon%20lighting%20el%20paso%20county%20courthouse%20professional%20architecture%20photography&width=800&height=600&seq=foreclosure-courthouse-026&orientation=landscape"
                alt="Texas courthouse foreclosure proceedings"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg mb-6"
              />
              
              <div className="bg-red-50 border border-red-200 p-6 rounded-xl">
                <h4 className="text-lg font-bold text-red-800 mb-3 flex items-center">
                  <i className="ri-error-warning-line mr-2"></i>
                  Texas Foreclosure Is Fast
                </h4>
                <p className="text-red-700">
                  Texas is a non-judicial foreclosure state, meaning lenders don&apos;t need court approval to foreclose. The entire process can be completed in as little as 60 days from the first notice. Unlike many states, Texas has no redemption period after the sale.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credit Score Impact Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How Missed Payments Affect Your <span className="text-teal-600">Credit Score</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding the credit impact helps you make informed decisions about your options
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=credit%20score%20report%20document%20showing%20declining%20numbers%20with%20red%20downward%20arrow%20financial%20charts%20and%20graphs%20on%20modern%20desk%20laptop%20displaying%20credit%20monitoring%20warm%20office%20lighting%20professional%20business%20photography&width=800&height=600&seq=credit-score-impact-027&orientation=landscape"
                alt="Credit score impact from missed mortgage payments"
                className="w-full h-80 object-cover object-top rounded-xl shadow-lg"
              />
            </div>

            <div>
              <div className="space-y-4">
                <div className="bg-white p-5 rounded-lg shadow-md">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">30 Days Late</span>
                    <span className="text-red-600 font-bold">-60 to -110 points</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-500 h-2 rounded-full" style={{width: '30%'}}></div>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-lg shadow-md">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">60 Days Late</span>
                    <span className="text-red-600 font-bold">-85 to -135 points</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-orange-500 h-2 rounded-full" style={{width: '50%'}}></div>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-lg shadow-md">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">90 Days Late</span>
                    <span className="text-red-600 font-bold">-105 to -155 points</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-red-500 h-2 rounded-full" style={{width: '70%'}}></div>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-lg shadow-md">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold text-gray-900">Foreclosure</span>
                    <span className="text-red-600 font-bold">-150 to -240 points</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-red-700 h-2 rounded-full" style={{width: '100%'}}></div>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-teal-50 rounded-lg border border-teal-200">
                <p className="text-teal-800 text-sm">
                  <strong>Recovery Time:</strong> A foreclosure stays on your credit report for 7 years. However, selling your home before foreclosure—even through a short sale—has less severe and shorter-lasting credit impacts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Options Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Your Options When You <span className="text-teal-600">Can&apos;t Pay</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              You have more choices than you might think—here are the most common solutions for El Paso homeowners
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-phone-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">1. Contact Your Lender</h3>
              <p className="text-gray-600 mb-4">
                This should be your first step. Lenders don&apos;t want to foreclose—it&apos;s expensive and time-consuming for them. Many offer hardship programs including:
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Forbearance (temporary payment pause)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Loan modification (new terms)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Repayment plans</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-refresh-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">2. Refinance Your Mortgage</h3>
              <p className="text-gray-600 mb-4">
                If you have equity and decent credit, refinancing might lower your monthly payment. However, this option becomes harder once you&apos;ve missed payments.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Lower interest rate possible</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Extend loan term</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Cash-out option for bills</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">3. Sell Your Home</h3>
              <p className="text-gray-600 mb-4">
                If you have equity, selling before foreclosure protects your credit and lets you walk away with cash. Even if you&apos;re behind on payments, <a href="/elpaso-tx/cash-home-buyers-el-paso" className="text-teal-600 hover:underline">cash home buyers in El Paso</a> can close quickly.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Avoid foreclosure on record</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Keep remaining equity</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Close in as little as 7 days</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-arrow-down-circle-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">4. Short Sale</h3>
              <p className="text-gray-600 mb-4">
                If you owe more than your home is worth, a short sale lets you sell for less than the mortgage balance with lender approval. This is better for your credit than foreclosure.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Less credit damage than foreclosure</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Debt may be forgiven</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Buy again sooner</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-key-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">5. Deed in Lieu</h3>
              <p className="text-gray-600 mb-4">
                You voluntarily transfer ownership to the lender to avoid foreclosure. This option requires lender approval and typically requires you to try selling first.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Faster than foreclosure</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>May include relocation assistance</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Cleaner credit report</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-scales-3-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">6. Bankruptcy</h3>
              <p className="text-gray-600 mb-4">
                Chapter 13 bankruptcy can stop foreclosure and let you catch up on payments over 3-5 years. This is a last resort but can save your home if you have steady income.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Automatic stay stops foreclosure</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Catch up over time</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span>Protect other assets</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Loan Modification Deep Dive */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Understanding <span className="text-teal-600">Loan Modification</span>
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                A loan modification permanently changes the terms of your mortgage to make payments more affordable. Unlike refinancing, you don&apos;t get a new loan—your existing loan is modified. This is often the best option for homeowners who want to keep their home but can&apos;t afford current payments.
              </p>
              
              <h3 className="text-xl font-bold text-gray-900 mb-4">What Can Be Modified?</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start">
                  <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Interest Rate:</strong> Reduced to lower monthly payments</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Loan Term:</strong> Extended to spread payments over more years</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Principal Balance:</strong> Sometimes reduced (rare)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-double-line text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Past-Due Amounts:</strong> Added to loan balance</span>
                </li>
              </ul>

              <div className="bg-teal-50 p-6 rounded-xl border border-teal-200">
                <h4 className="font-bold text-teal-800 mb-2">Pro Tip</h4>
                <p className="text-teal-700">
                  Document your hardship thoroughly. Lenders need to see proof of financial difficulty (job loss, medical bills, divorce) and evidence that you can afford modified payments. Keep records of all communication with your lender.
                </p>
              </div>
            </div>

            <div>
              <img
                src="https://readdy.ai/api/search-image?query=homeowner%20meeting%20with%20mortgage%20lender%20representative%20at%20bank%20office%20discussing%20loan%20modification%20paperwork%20on%20desk%20professional%20business%20setting%20warm%20lighting%20showing%20negotiation%20and%20hope&width=800&height=600&seq=loan-modification-028&orientation=landscape"
                alt="Homeowner discussing loan modification with lender"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Forbearance Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=calendar%20showing%20paused%20mortgage%20payments%20with%20relief%20symbol%20family%20looking%20hopeful%20at%20kitchen%20table%20financial%20documents%20organized%20neatly%20warm%20natural%20lighting%20showing%20temporary%20relief%20professional%20photography&width=800&height=600&seq=forbearance-relief-029&orientation=landscape"
                alt="Mortgage forbearance providing temporary relief"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Mortgage <span className="text-teal-600">Forbearance</span> Explained
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Forbearance is a temporary pause or reduction in your mortgage payments. It&apos;s designed for short-term hardships like job loss, medical emergencies, or natural disasters. During forbearance, your lender agrees not to pursue foreclosure.
              </p>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mb-6">
                <h4 className="font-bold text-amber-800 mb-2">Important: Forbearance Is Not Forgiveness</h4>
                <p className="text-amber-700">
                  You still owe all the payments you missed during forbearance. At the end of the forbearance period, you&apos;ll need to repay them through a lump sum, repayment plan, or loan modification.
                </p>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-4">Forbearance Repayment Options</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <span className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">1</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">Lump Sum</h4>
                    <p className="text-gray-600">Pay all missed payments at once when forbearance ends</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">2</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">Repayment Plan</h4>
                    <p className="text-gray-600">Add extra to each payment until caught up</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">3</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">Loan Modification</h4>
                    <p className="text-gray-600">Add missed payments to loan balance with new terms</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">4</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">Deferral</h4>
                    <p className="text-gray-600">Move missed payments to end of loan term</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When Selling Is the Best Option */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              When Selling Your Home Is the <span className="text-teal-600">Best Option</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Sometimes the smartest financial decision is to sell before foreclosure—here&apos;s when it makes sense
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-check-double-line text-teal-600 mr-3"></i>
                Selling Makes Sense When...
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700">You have equity in your home and can walk away with cash</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700">Your financial situation won&apos;t improve enough to afford payments</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700">You need to relocate for work or family reasons</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700">The home needs <a href="/elpaso-tx/cant-afford-repairs-to-sell-house" className="text-teal-600 hover:underline">repairs you can&apos;t afford</a></span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700">You want to protect your credit from foreclosure damage</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700">You&apos;re going through divorce and need to divide assets</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-teal-600 mr-3 mt-1"></i>
                  <span className="text-gray-700">You&apos;ve <a href="/inherited-homes" className="text-teal-600 hover:underline">inherited a property</a> you can&apos;t maintain</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-time-line text-teal-600 mr-3"></i>
                Benefits of Selling Before Foreclosure
              </h3>
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Protect Your Credit</h4>
                  <p className="text-gray-600">A regular sale has minimal credit impact compared to foreclosure&apos;s 150-240 point drop that stays on your report for 7 years.</p>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Keep Your Equity</h4>
                  <p className="text-gray-600">In foreclosure, any equity above what you owe often goes to fees and costs. Selling lets you keep the difference.</p>
                </div>
                <div className="border-b border-gray-200 pb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Control the Timeline</h4>
                  <p className="text-gray-600">You choose when to move rather than being evicted. Cash buyers can close in 7-14 days if you need to move fast.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Buy Again Sooner</h4>
                  <p className="text-gray-600">After foreclosure, you may wait 3-7 years to buy again. After a regular sale, you can buy immediately if qualified.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comparing Your <span className="text-teal-600">Exit Options</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how different solutions stack up in terms of credit impact, timeline, and financial outcome
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
              <thead className="bg-gray-900 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Option</th>
                  <th className="px-6 py-4 text-left font-semibold">Credit Impact</th>
                  <th className="px-6 py-4 text-left font-semibold">Timeline</th>
                  <th className="px-6 py-4 text-left font-semibold">Walk Away With</th>
                  <th className="px-6 py-4 text-left font-semibold">Buy Again</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Sell to Cash Buyer</td>
                  <td className="px-6 py-4 text-green-600">Minimal</td>
                  <td className="px-6 py-4">7-14 days</td>
                  <td className="px-6 py-4">Equity (cash)</td>
                  <td className="px-6 py-4">Immediately</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Traditional Sale</td>
                  <td className="px-6 py-4 text-green-600">Minimal</td>
                  <td className="px-6 py-4">30-90 days</td>
                  <td className="px-6 py-4">Equity (cash)</td>
                  <td className="px-6 py-4">Immediately</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Short Sale</td>
                  <td className="px-6 py-4 text-yellow-600">Moderate (-50 to -150)</td>
                  <td className="px-6 py-4">60-120 days</td>
                  <td className="px-6 py-4">Nothing (debt forgiven)</td>
                  <td className="px-6 py-4">2-4 years</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Deed in Lieu</td>
                  <td className="px-6 py-4 text-orange-600">Significant (-100 to -150)</td>
                  <td className="px-6 py-4">30-90 days</td>
                  <td className="px-6 py-4">Possible relocation $</td>
                  <td className="px-6 py-4">2-4 years</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Foreclosure</td>
                  <td className="px-6 py-4 text-red-600">Severe (-150 to -240)</td>
                  <td className="px-6 py-4">60-120 days (TX)</td>
                  <td className="px-6 py-4">Nothing</td>
                  <td className="px-6 py-4">3-7 years</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Chapter 13 Bankruptcy</td>
                  <td className="px-6 py-4 text-red-600">Severe (-130 to -200)</td>
                  <td className="px-6 py-4">3-5 years</td>
                  <td className="px-6 py-4">Keep home</td>
                  <td className="px-6 py-4">2-4 years</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Texas-Specific Information */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Texas-Specific <span className="text-teal-600">Foreclosure Laws</span>
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Texas has some of the fastest foreclosure timelines in the country. Understanding these laws helps you know exactly how much time you have to act and what protections exist.
              </p>

              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-gavel-line text-teal-600 mr-3"></i>
                    Non-Judicial Foreclosure
                  </h3>
                  <p className="text-gray-600">
                    Texas allows lenders to foreclose without going to court, making the process faster and less expensive for them. This means you have less time to respond than in judicial foreclosure states.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-calendar-line text-teal-600 mr-3"></i>
                    First Tuesday Sales
                  </h3>
                  <p className="text-gray-600">
                    All Texas foreclosure auctions occur on the first Tuesday of each month at the county courthouse. If you&apos;re facing <a href="/elpaso-tx/el-paso-tax-foreclosure-information-deadlines" className="text-teal-600 hover:underline">tax foreclosure deadlines in El Paso</a>, this schedule is critical to understand.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-close-circle-line text-teal-600 mr-3"></i>
                    No Redemption Period
                  </h3>
                  <p className="text-gray-600">
                    Unlike many states, Texas has no redemption period after foreclosure sale. Once your home is sold, you cannot buy it back. The new owner can begin eviction immediately.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-home-line text-teal-600 mr-3"></i>
                    Homestead Protection
                  </h3>
                  <p className="text-gray-600">
                    Texas homestead laws protect your primary residence from most creditors—but not from your mortgage lender or <a href="/elpaso-tx/owing-too-much-property-tax" className="text-teal-600 hover:underline">property tax authorities</a>.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <img
                src="https://readdy.ai/api/search-image?query=texas%20state%20flag%20waving%20in%20front%20of%20courthouse%20building%20with%20legal%20scales%20of%20justice%20symbol%20el%20paso%20county%20government%20architecture%20warm%20golden%20hour%20lighting%20professional%20photography&width=800&height=900&seq=texas-foreclosure-laws-030&orientation=portrait"
                alt="Texas courthouse representing state foreclosure laws"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Common Mistakes to <span className="text-red-600">Avoid</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don&apos;t make these errors that can cost you your home, your equity, or your credit
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-red-50 p-6 rounded-xl border border-red-200">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-eye-off-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Ignoring the Problem</h3>
              <p className="text-gray-600">
                Many homeowners avoid opening mail or answering calls from their lender. This only makes things worse—the sooner you communicate, the more options you have.
              </p>
            </div>

            <div className="bg-red-50 p-6 rounded-xl border border-red-200">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Waiting Too Long</h3>
              <p className="text-gray-600">
                Texas foreclosure moves fast. If you wait until you receive the Notice of Sale, you may only have 21 days left. Start exploring options at the first sign of trouble.
              </p>
            </div>

            <div className="bg-red-50 p-6 rounded-xl border border-red-200">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Paying Scammers</h3>
              <p className="text-gray-600">
                Beware of &quot;foreclosure rescue&quot; companies that charge upfront fees. Legitimate help is often free through HUD-approved counselors or your lender&apos;s loss mitigation department.
              </p>
            </div>

            <div className="bg-red-50 p-6 rounded-xl border border-red-200">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-home-gear-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Abandoning the Property</h3>
              <p className="text-gray-600">
                Moving out before foreclosure is complete can lead to vandalism, code violations, and additional liability. Stay until you have a plan in place.
              </p>
            </div>

            <div className="bg-red-50 p-6 rounded-xl border border-red-200">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-bank-card-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Using Retirement Funds</h3>
              <p className="text-gray-600">
                Raiding your 401(k) or IRA to save your home often leads to penalties, taxes, and still losing the house. Protect your retirement—explore other options first.
              </p>
            </div>

            <div className="bg-red-50 p-6 rounded-xl border border-red-200">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-file-unknow-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Not Understanding Documents</h3>
              <p className="text-gray-600">
                Signing loan modification or forbearance agreements without understanding the terms can create new problems. Get everything in writing and ask questions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-teal-600">Questions</span>
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3">How many missed payments before foreclosure starts in Texas?</h3>
              <p className="text-gray-600">
                Technically, your lender can begin foreclosure proceedings after just one missed payment, but most wait until you&apos;re 90-120 days behind. Federal law requires lenders to wait at least 120 days before filing the first foreclosure notice on most mortgages.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Can I sell my house if I&apos;m behind on payments?</h3>
              <p className="text-gray-600">
                Yes, absolutely. As long as you still own the home (foreclosure hasn&apos;t been completed), you can sell it. If you have equity, you can pay off the mortgage and keep the difference. If you owe more than it&apos;s worth, you may need lender approval for a short sale. Many homeowners choose to <a href="/elpaso-tx/sell-house-fast" className="text-teal-600 hover:underline">sell their house fast in El Paso</a> to avoid foreclosure.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Will my lender work with me if I can&apos;t pay?</h3>
              <p className="text-gray-600">
                Most lenders prefer to work with you rather than foreclose. Foreclosure is expensive and time-consuming for them. Contact your lender&apos;s loss mitigation department as soon as you know you&apos;ll have trouble paying. Be prepared to document your hardship and show how you plan to resolve it.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3">How long can I stay in my house without paying the mortgage?</h3>
              <p className="text-gray-600">
                In Texas, the foreclosure process typically takes 60-90 days from the first notice, though it can be longer if you&apos;re working with your lender on alternatives. After the foreclosure sale, the new owner can begin eviction proceedings immediately—there&apos;s no redemption period in Texas.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3">What happens to my equity if my home is foreclosed?</h3>
              <p className="text-gray-600">
                If your home sells at foreclosure auction for more than you owe (including fees and costs), you&apos;re entitled to the surplus. However, foreclosure sales often bring less than market value, and fees can eat up any equity. Selling before foreclosure typically preserves more of your equity.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Can I get a mortgage after foreclosure?</h3>
              <p className="text-gray-600">
                Yes, but you&apos;ll need to wait. For conventional loans, the waiting period is typically 7 years. FHA loans may be available after 3 years with extenuating circumstances. VA loans require a 2-year wait. Selling before foreclosure can help you qualify for a new mortgage much sooner.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Should I hire a lawyer if I&apos;m facing foreclosure?</h3>
              <p className="text-gray-600">
                While not required, a foreclosure attorney can help you understand your rights, negotiate with your lender, and ensure all legal requirements are followed. Many offer free consultations. HUD-approved housing counselors also provide free assistance and can help you explore all options.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-3">What if I have tenants in my property?</h3>
              <p className="text-gray-600">
                If you&apos;re a landlord facing foreclosure on a rental property, your tenants have certain protections under federal law. However, selling the property—even with tenants in place—is often the best solution. We <a href="/elpaso-tx/we-buy-houses-with-tenants" className="text-teal-600 hover:underline">buy houses with tenants</a> and can close quickly regardless of the rental situation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Helpful <span className="text-teal-600">Resources</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Free assistance and information for El Paso homeowners facing mortgage difficulties
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl shadow-lg text-center">
              <img
                src="https://readdy.ai/api/search-image?query=hud%20approved%20housing%20counselor%20meeting%20with%20homeowner%20at%20desk%20reviewing%20documents%20professional%20office%20setting%20warm%20lighting%20showing%20helpful%20guidance%20and%20support&width=400&height=300&seq=hud-counselor-031&orientation=landscape"
                alt="HUD-approved housing counselor"
                className="w-full h-48 object-cover object-top rounded-lg mb-6"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">HUD Housing Counselors</h3>
              <p className="text-gray-600 mb-4">
                Free, HUD-approved counselors can help you understand your options and negotiate with your lender.
              </p>
              <a
                href="https://www.hud.gov/findacounselor"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-600 font-semibold hover:underline"
              >
                Find a Counselor →
              </a>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl shadow-lg text-center">
              <img
                src="https://readdy.ai/api/search-image?query=texas%20state%20assistance%20program%20office%20building%20with%20helpful%20staff%20member%20at%20reception%20desk%20warm%20welcoming%20environment%20professional%20government%20office%20photography&width=400&height=300&seq=texas-assistance-032&orientation=landscape"
                alt="Texas homeowner assistance programs"
                className="w-full h-48 object-cover object-top rounded-lg mb-6"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Texas Homeowner Assistance</h3>
              <p className="text-gray-600 mb-4">
                The Texas Department of Housing offers programs to help homeowners catch up on mortgage payments.
              </p>
              <a
                href="https://www.tdhca.state.tx.us/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-600 font-semibold hover:underline"
              >
                Learn More →
              </a>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl shadow-lg text-center">
              <img
                src="https://readdy.ai/api/search-image?query=legal%20aid%20attorney%20meeting%20with%20client%20at%20desk%20reviewing%20foreclosure%20documents%20professional%20law%20office%20setting%20warm%20lighting%20showing%20legal%20assistance&width=400&height=300&seq=legal-aid-033&orientation=landscape"
                alt="Legal aid services"
                className="w-full h-48 object-cover object-top rounded-lg mb-6"
              />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Legal Aid of Northwest Texas</h3>
              <p className="text-gray-600 mb-4">
                Free legal assistance for qualifying El Paso residents facing foreclosure or housing issues.
              </p>
              <a
                href="https://www.lanwt.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-600 font-semibold hover:underline"
              >
                Get Help →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need to Sell Your Home <span className="text-teal-400">Before Foreclosure?</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            We buy houses in any condition, even if you&apos;re behind on payments. Get a fair cash offer within 24 hours and close in as little as 7 days. No fees, no commissions, no repairs needed.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:+1-915-206-5814"
              className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (915) 206-5814
            </a>
            <a
              href="/get-cash-offer-for-my-property"
              className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Your Cash Offer
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
          <p className="text-gray-400 mt-6 text-sm">
            Confidential consultation • No obligation • Available 7 days a week
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CantPayMortgagePage;
